import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Tournament } from '../models/tournament';
import { map } from 'rxjs/operators';
import { TournamentNews } from '../models/tournament-news';
import { Game, GameStatuses } from '../models/game';
import { GameTimelineStages } from '../models/game-timeline-stages';
import { TournamentTeam } from '../models/tournament-team';
import { TournamentStage } from '../models/tournament-stage';
import { Playoff } from '../models/playoff';
import { PaginatedResponse } from './paginated-response.interface';
import { BasketballStatistic } from '../models/basketball-statistic';
import { VolleyballStatistic } from '../models/volleyball-statistic';
import { TournamentTeamUser } from '../models/tournament-team-user';
import { TournamentStageTeam } from '../models/tournament-stage-team';
import { TournamentEvent, TournamentEventTypes } from '../models/tournament-event';
import { ConfigService } from '../services/config.service';
import { HockeyStatistic } from '../models/hockey-statistic';
import { FootballStatistic } from '../models/football-statistic';
import { LeagueUserPermissions } from '../models/league-user';
import { HandballStatistic } from '../models/handball-statistic';
import { RugbyStatistic } from '../models/rugby-statistic';
import { TournamentGroup } from '../models/tournament-group';
import { TeamAchievement } from '../models/team-achievement';
import { WaterpoloStatistic } from '../models/waterpolo-statistic';
import { TournamentRound } from '../models/tournament-round';
import { TournamentDivision } from '../models/tournament-division';
import { UserGender } from '../models/user';
import { GameUserLimitations } from '../models/game-user-limitation';
import { GameHockeyPosition } from '../models/hockey-profile';
import { GameFootballPosition } from '../models/football-profile';
import { GameHandballPosition } from '../models/handball-profile';
import { GameWaterpoloPosition } from '../models/waterpolo-profile';
import { GameBasketballPosition } from '../models/basketball-profile';
import { GameVolleyballPosition } from '../models/volleyball-profile';
import { GameRugbyPosition } from '../models/rugby-profile';
import { WrestballStatistic } from '../models/wrestball-statistic';
import { TournamentRoundTeam } from '../models/tournament-round-team';

export interface TournamentGamesFilters {
  tournamentIds?: number[];
  tournamentSeasonId?: number;
  tournamentStageId?: number;
  tournamentRoundId?: number;
  tournamentGroupId?: number;
  divisionId?: number;
  tournamentTour?: number;
  teamId?: number;
  playoffStage?: number;
  playoffRound?: number;
  playoffId?: number;
  isPlayoff?: boolean;
  status?: GameStatuses;
  statuses?: GameStatuses[];
  tournamentCourtId?: number;
  leaguePlayerId?: number;
  userId?: number;
  query?: string;
  date_from?: string;
  date_to?: string;
}

export type StatisticGroupByTypes = 'team' | 'user' | 'team_user' | 'tournament_team' | 'league_player' | 'tournament_team_user'
  | 'month' | 'win_loses';

export interface StatisticFilters {
  league_id?: number;
  tournament_id?: number;
  tournament_ids?: number[];
  tournament_season_id?: number;
  tournament_season_ids?: number[];
  tournament_team_id?: number;
  league_player_id?: number;
  tournament_team_user_id?: number;
  team_id?: number;
  team_user_id?: number;
  user_id?: number;
  tournament_stage_id?: number;
  tournament_round_id?: number;
  division_id?: number;
  tournament_group_id?: number;
  tournament_tour?: number;
  playoff_stage?: number;
  playoff_round?: number;
  playoff_id?: number;
  is_playoff?: boolean;
  date_from?: string;
  date_to?: string;
  query?: string;
  gender?: UserGender;
  basketball_position?: GameBasketballPosition;
  volleyball_position?: GameVolleyballPosition;
  hockey_position?: GameHockeyPosition;
  football_position?: GameFootballPosition;
  handball_position?: GameHandballPosition;
  waterpolo_position?: GameWaterpoloPosition;
  rugby_position?: GameRugbyPosition;
  league_player_field_value?: {[key: string]: string};
  tournament_team_field_value?: {[key: string]: string};
  per_game?: boolean;
  group_by: StatisticGroupByTypes;
  only_closed?: boolean;
  check_ignore_game?: boolean;
  min_games_count?: number;
  order_by?: string;
  page?: number;
  size?: number;
}

export interface StatisticLeadersFilters {
  tournament_id?: number;
  tournament_ids?: number[];
  tournament_season_id?: number;
  tournament_team_id?: number;
  team_id?: number;
  tournament_stage_id?: number;
  tournament_tour?: number;
  playoff_stage?: number;
  playoff_id?: number;
  date_from?: string;
  date_to?: string;
  per_game?: boolean;
  group_by: StatisticGroupByTypes;
  size?: number;
  fields: string[];
}

export interface TournamentEventsListFilter {
  event?: TournamentEventTypes;
  search?: string;
  tournamentTour?: number;
}

@Injectable({providedIn: 'root'})
export class TournamentApi {
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {
  }

  async getById(tournamentId: number): Promise<Tournament> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/`)
      .pipe(map(result => Tournament.toFront(result)))
      .toPromise();
  }

  async getByAlias(leagueId: number, alias: string): Promise<Tournament> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/tournaments/${alias}/`)
      .pipe(map(result => Tournament.toFront(result)))
      .toPromise();
  }

  async getSeasonTournaments(seasonTournamentId: number): Promise<Tournament[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/season_tournament/${seasonTournamentId}/tournaments/`)
      .pipe(map(result => Tournament.toFront(result)))
      .toPromise();
  }

  async getNews(tournamentId: number): Promise<TournamentNews[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/news/`)
      .pipe(map(result => TournamentNews.toFront(result)))
      .toPromise();
  }

  async getEvents(tournamentId: number, page: number, size: number, filters?: TournamentEventsListFilter): Promise<PaginatedResponse<TournamentEvent[]>> {
    let params = new HttpParams().set('page', page.toString())
      .set('size', size.toString());
    if (filters) {
      if (filters.event) {
        params = params.set('event', TournamentEventTypes[filters.event]);
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.tournamentTour) {
        params = params.set('tournament_tour', filters.tournamentTour.toString());
      }
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/events/`, {params, observe: 'response'})
      .pipe(map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: TournamentEvent.toFront(result.body)
      })))
      .toPromise();
  }

  async getTournamentStages(tournamentId: number): Promise<TournamentStage[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/stages/`).pipe(
      map(data => TournamentStage.toFront(data))
    ).toPromise();
  }

  async getTournamentRounds(tournamentId: number): Promise<TournamentRound[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/rounds/`).pipe(
      map(data => TournamentRound.toFront(data)),
    ).toPromise();
  }

  async getTournamentDivisions(tournamentId: number): Promise<TournamentDivision[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/divisions/`).pipe(
      map(data => TournamentDivision.toFront(data)),
    ).toPromise();
  }

  async getPlayoff(tournamentId: number): Promise<Playoff[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/playoff/`).pipe(
      map(result => Playoff.toFront(result))
    ).toPromise();
  }

  async getPlayoffGames(playoffId: number, playoffNumber?: string): Promise<Game[]> {
    let params = new HttpParams();
    if (playoffNumber) {
      params = params.set('playoff_number', playoffNumber);
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_playoff/${playoffId}/games/`,
      {params}
    ).pipe(
      map(result => Game.toFront(result))
    ).toPromise();
  }

  async getGamesStages(tournamentId: number, roundId?: number): Promise<GameTimelineStages> {
    let params = new HttpParams();
    if (roundId) {
      params = params.set('tournament_round_id', roundId.toString());
    }
    return this.httpClient.get(
      `${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/games_stages/`,
      {params}
    )
      .pipe(map(data => GameTimelineStages.toFront(data)))
      .toPromise();
  }

  async getGames(tournamentId: number, page: number, size: number, filters: TournamentGamesFilters = {}): Promise<PaginatedResponse<Game[]>> {
    let params = new HttpParams().set('page', page.toString());
    if (size) {
      params = params.set('size', size.toString());
    }
    params = applyGamesFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/games/`, {params, observe: 'response'})
      .pipe(map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: Game.toFront(result.body)
      }))).toPromise();
  }

  async getGamesDates(tournamentId: number): Promise<any> {
    return this.httpClient.get(
      `${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/games_dates/`,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: result.body
      }))
    ).toPromise();
  }

  async getGroups(tournamentId: number): Promise<TournamentGroup[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/groups/`)
      .pipe(map(result => TournamentGroup.toFront(result)))
      .toPromise();
  }

  async getTeams(tournamentId: number, groupId?: number, short?: boolean, micro?: boolean): Promise<TournamentTeam[]> {
    let params = new HttpParams();
    if (groupId) {
      params = params.set('group_id', groupId.toString());
    }
    if (short) {
      params = params.set('short', '1');
    }
    if (micro) {
      params = params.set('micro', '1');
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/teams/`, {params})
      .pipe(map(result => TournamentTeam.toFront(result)))
      .toPromise();
  }

  async getStageTeams(tournamentId: number): Promise<TournamentStageTeam[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/stage_teams/`)
      .pipe(map(result => TournamentStageTeam.toFront(result)))
      .toPromise();
  }

  async getTeamsAchievements(tournamentId: number): Promise<TeamAchievement[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/achievements/`)
      .pipe(map(result => TeamAchievement.toFront(result)))
      .toPromise();
  }

  async getTeamById(tournamentTeamId: number): Promise<TournamentTeam> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team/${tournamentTeamId}/`)
      .pipe(map(result => TournamentTeam.toFront(result)))
      .toPromise();
  }

  async getTeamRoundTeams(tournamentTeamId: number): Promise<TournamentRoundTeam[]> {
    return this.httpClient.get<object[]>(
      `${this.configService.get('apiUrl')}/api/v1/tournament_team/${tournamentTeamId}/tournament_round_teams/`
    ).pipe(
      map(response => TournamentRoundTeam.toFront(response))
    ).toPromise();
  }

  async getUserById(userId: number): Promise<TournamentTeamUser> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team_user/${userId}/`)
      .pipe(map(result => TournamentTeamUser.toFront(result)))
      .toPromise();
  }

  async getUserGames(tournamentUserId: number): Promise<Game[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team_user/${tournamentUserId}/games/`)
      .pipe(map(result => Game.toFront(result)))
      .toPromise();
  }

  async downloadTable(tournamentId: number, format: 'pdf' | 'xlsx', isChess?: boolean): Promise<any> {
    let httpParams = new HttpParams().set('file_type', format);
    if (isChess) {
      httpParams = httpParams.set('isChess', true);
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/table_file/`, {
      params: httpParams,
      responseType: 'blob'
    }).toPromise();
  }

  async getBasketballStatisticLeaders(filters: StatisticLeadersFilters): Promise<{[key: string]: BasketballStatistic[]}> {
    let params = new HttpParams();
    params = applyStatisticLeadersFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/basketball_statistic_leaders/`, {params}).pipe(
      map((result: {[key: string]: any[]}) => {
        for (const key of Object.keys(result)) {
          result[key] = BasketballStatistic.toFront(result[key]);
        }
        return result;
      })
    ).toPromise();
  }

  async getVolleyballStatisticLeaders(filters: StatisticLeadersFilters): Promise<{[key: string]: VolleyballStatistic[]}> {
    let params = new HttpParams();
    params = applyStatisticLeadersFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/volleyball_statistic_leaders/`, {params}).pipe(
      map((result: {[key: string]: any[]}) => {
        for (const key of Object.keys(result)) {
          result[key] = VolleyballStatistic.toFront(result[key]);
        }
        return result;
      })
    ).toPromise();
  }

  async getHockeyStatisticLeaders(filters: StatisticLeadersFilters): Promise<{[key: string]: HockeyStatistic[]}> {
    let params = new HttpParams();
    params = applyStatisticLeadersFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/hockey_statistic_leaders/`, {params}).pipe(
      map((result: {[key: string]: any[]}) => {
        for (const key of Object.keys(result)) {
          result[key] = HockeyStatistic.toFront(result[key]);
        }
        return result;
      })
    ).toPromise();
  }

  async getFootballStatisticLeaders(filters: StatisticLeadersFilters): Promise<{[key: string]: FootballStatistic[]}> {
    let params = new HttpParams();
    params = applyStatisticLeadersFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/football_statistic_leaders/`, {params}).pipe(
      map((result: {[key: string]: any[]}) => {
        for (const key of Object.keys(result)) {
          result[key] = FootballStatistic.toFront(result[key]);
        }
        return result;
      })
    ).toPromise();
  }

  async getHandballStatisticLeaders(filters: StatisticLeadersFilters): Promise<{[key: string]: HandballStatistic[]}> {
    let params = new HttpParams();
    params = applyStatisticLeadersFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/handball_statistic_leaders/`, {params}).pipe(
      map((result: {[key: string]: any[]}) => {
        for (const key of Object.keys(result)) {
          result[key] = HandballStatistic.toFront(result[key]);
        }
        return result;
      })
    ).toPromise();
  }

  async getWaterpoloStatisticLeaders(filters: StatisticLeadersFilters): Promise<{[key: string]: WaterpoloStatistic[]}> {
    let params = new HttpParams();
    params = applyStatisticLeadersFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/waterpolo_statistic_leaders/`, {params}).pipe(
      map((result: {[key: string]: any[]}) => {
        for (const key of Object.keys(result)) {
          result[key] = WaterpoloStatistic.toFront(result[key]);
        }
        return result;
      })
    ).toPromise();
  }

  async getBasketballStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<BasketballStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/basketball_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: BasketballStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getVolleyballStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<VolleyballStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/volleyball_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: VolleyballStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getHockeyStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<HockeyStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/hockey_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: HockeyStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getFootballStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<FootballStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/football_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: FootballStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getHandballStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<HandballStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/handball_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: HandballStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getRugbyStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<RugbyStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/rugby_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: RugbyStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getWaterpoloStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<WaterpoloStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/waterpolo_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: WaterpoloStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getWrestballStatistic(filters?: StatisticFilters): Promise<PaginatedResponse<WrestballStatistic[]>> {
    const data = applyStatisticFilters(filters);

    return this.httpClient.post(
      `${this.configService.get('apiUrl')}/api/v1/wrestball_statistic/`,
      data,
      {observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: WrestballStatistic.toFront(result.body)
      }))
    ).toPromise();
  }

  async getTournamentTeamUsers(tournamentTeamId: number, includes: string[] = []): Promise<TournamentTeamUser[]> {
    let params = new HttpParams();
    for (const include of includes) {
      params = params.append('include', include);
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team/${tournamentTeamId}/users/`, {params}).pipe(
      map(result => TournamentTeamUser.toFront(result))
    ).toPromise();
  }

  async myPermission(tournamentId: number): Promise<LeagueUserPermissions[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/permission/`).pipe(
      map((data: any) => (data.permissions || []).map(item => LeagueUserPermissions[item]))
    ).toPromise();
  }

  async getTournamentTeamTournaments(tournamentTeamId: number): Promise<Tournament[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team/${tournamentTeamId}/tournaments/`).pipe(
      map(result => Tournament.toFront(result))
    ).toPromise();
  }

  async getTournamentUserTournaments(tournamentTeamUserId: number): Promise<Tournament[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team_user/${tournamentTeamUserId}/tournaments/`).pipe(
      map(result => Tournament.toFront(result))
    ).toPromise();
  }

  async getTournamentTeamUsersLimitation(tournamentTeamId: number): Promise<GameUserLimitations[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team/${tournamentTeamId}/users_limitation/`).pipe(
      map(result => GameUserLimitations.toFront(result)),
    ).toPromise();
  }

  async downloadTeamSanctionList(tournamentTeamId: number, fileType: 'pdf' | 'xlsx' | 'html'): Promise<any> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_team/${tournamentTeamId}/sanction_list_file/?file_type=${fileType}`, {
      params: new HttpParams().set('file_type', fileType),
      responseType: 'blob'
    }).toPromise();
  }
}

export function applyGamesFilters(filters: TournamentGamesFilters, params: HttpParams): HttpParams {
  if (filters.tournamentIds) {
    params = params.set('tournament_ids', filters.tournamentIds.join(','));
  }
  if (filters.tournamentStageId) {
    params = params.set('tournament_stage_id', filters.tournamentStageId.toString());
  }
  if (filters.tournamentRoundId) {
    params = params.set('tournament_round_id', filters.tournamentRoundId.toString());
  }
  if (filters.tournamentGroupId) {
    params = params.set('tournament_group_id', filters.tournamentGroupId.toString());
  }
  if (filters.divisionId) {
    params = params.set('division_id', filters.divisionId.toString());
  }
  if (filters.tournamentTour) {
    params = params.set('tournament_tour', filters.tournamentTour.toString());
  }
  if (filters.playoffStage) {
    params = params.set('playoff_stage', filters.playoffStage.toString());
  }
  if (filters.playoffRound) {
    params = params.set('playoff_round', filters.playoffRound.toString());
  }
  if (filters.teamId) {
    params = params.set('team_id', filters.teamId.toString());
  }
  if (filters.status) {
    params = params.set('status', GameStatuses[filters.status]);
  }
  if (filters.statuses) {
    const statuses = filters.statuses.map(i => GameStatuses[i]);
    params = params.set('statuses', statuses.join(','));
  }
  if (filters.tournamentCourtId) {
    params = params.set('tournament_court_id', filters.tournamentCourtId.toString());
  }
  if (filters.leaguePlayerId) {
    params = params.set('league_player_id', filters.leaguePlayerId.toString());
  }
  if (filters.userId) {
    params = params.set('user_id', filters.userId.toString());
  }
  if (filters.query) {
    params = params.set('query', filters.query);
  }
  if (filters.date_from) {
    params = params.set('date_from', filters.date_from);
  }
  if (filters.date_to) {
    params = params.set('date_to', filters.date_to);
  }
  if (filters.isPlayoff !== undefined && filters.isPlayoff !== null) {
    params = params.set('is_playoff', filters.isPlayoff ? '1' : '0');
  }
  return params;
}

export function applyStatisticFilters(filters: StatisticFilters): object {
  const rawFilters = {};
  for (const key of Object.keys(filters)) {
    if (filters[key]) {
      if (key === 'basketball_position') {
        rawFilters[key] = GameBasketballPosition[filters[key]];
      } else if (key === 'volleyball_position') {
        rawFilters[key] = GameVolleyballPosition[filters[key]];
      } else if (key === 'hockey_position') {
        rawFilters[key] = GameHockeyPosition[filters[key]];
      } else if (key === 'football_position') {
        rawFilters[key] = GameFootballPosition[filters[key]];
      } else if (key === 'handball_position') {
        rawFilters[key] = GameHandballPosition[filters[key]];
      } else if (key === 'waterpolo_position') {
        rawFilters[key] = GameWaterpoloPosition[filters[key]];
      } else if (key === 'rugby_position') {
        rawFilters[key] = GameRugbyPosition[filters[key]];
      } else {
        rawFilters[key] = filters[key];
      }
    }
  }

  if (filters.per_game !== undefined) {
    rawFilters['per_game'] = filters.per_game ? '1' : '0';
  }

  if (filters.tournament_ids) {
    rawFilters['tournament_ids'] = filters.tournament_ids.join(',');
  }

  if (filters.tournament_season_ids) {
    rawFilters['tournament_season_ids'] = filters.tournament_season_ids.join(',');
  }

  if (filters.is_playoff !== undefined && filters.is_playoff !== null) {
    rawFilters['is_playoff'] = filters.is_playoff ? '1' : '0';
  }

  if (filters.only_closed !== undefined && filters.only_closed !== null) {
    rawFilters['only_closed'] = filters.only_closed ? '1' : '0';
  }

  if (filters.check_ignore_game !== undefined && filters.check_ignore_game !== null) {
    rawFilters['check_ignore_game'] = filters.check_ignore_game ? '1' : '0';
  }

  return rawFilters;
}

export function applyStatisticLeadersFilters(filters: StatisticLeadersFilters, params: HttpParams): HttpParams {
  for (const key of Object.keys(filters)) {
    if (filters[key]) {
      params = params.set(key, filters[key]);
    }
  }

  if (filters.per_game !== undefined) {
    params = params.set('per_game', filters.per_game ? '1' : '0');
  }

  if (filters.tournament_ids) {
    params = params.set('tournament_ids', filters.tournament_ids.join(','));
  }
  if (filters.fields) {
    params = params.set('fields', filters.fields.join(','));
  }
  return params;
}
